import { useState, useEffect, useRef } from 'react';
import http from '../http/http';

interface PromStatusResult {
  due: boolean;
  phase?: 'baseline' | 'retest';
  baselineDate?: string;
  lastAssessmentDate?: string;
  lastReportDate?: string;
  daysUntilNext?: number;
}

const STORAGE_KEY = 'prom_status_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

const activeSetters = new Set<(value: PromStatusResult) => void>();

let setStatusRef: ((value: PromStatusResult) => void) | null = null;

async function fetchPromStatus(setStatus: (value: PromStatusResult) => void, forceRefresh = false) {
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached && !forceRefresh) {
    const parsed = JSON.parse(cached);
    if (Date.now() - parsed.timestamp < CACHE_TTL) {
      setStatus(parsed.data);
      return;
    }
  }

  const data = await http.get('/api/prom/status');
  if (data && typeof data === 'object' && 'success' in data && !data.success) {
    throw new Error(data.message || 'Request failed');
  }
  const result: PromStatusResult = data || { due: false };
  setStatus(result);
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ data: result, timestamp: Date.now() }));
}

export async function refreshPromStatusExternally() {
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached) {
    localStorage.removeItem(STORAGE_KEY);
  }

  const data = await http.get('/api/prom/status');
  if (data && typeof data === 'object' && 'success' in data && !data.success) {
    return;
  }
  const result: PromStatusResult = data || { due: false };
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ data: result, timestamp: Date.now() }));

  for (const setter of Array.from(activeSetters)) {
    setter(result);
  }
  if (setStatusRef) {
    setStatusRef(result);
  }
}

export function usePromStatus() {
  const [status, setStatus] = useState<PromStatusResult | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      await fetchPromStatus(setStatus, false);
    } catch {
      setStatus({ due: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    activeSetters.add(setStatus);
    setStatusRef = setStatus;
    fetchStatus();
    return () => {
      activeSetters.delete(setStatus);
      if (setStatusRef === setStatus) setStatusRef = null;
    };
  }, []);

  return { status, loading, refresh: fetchStatus };
}
